/**
 * <s-dert-intro> — DERT entrance.
 * Plays the intro video immediately. Its black is made see-through (screen blend),
 * the welcome line fades in at data-text-at seconds, then the backdrop clears so the
 * logo floats over the store, and a copy of the header logo glides into the header.
 * Plays whenever a visitor arrives from outside the store or reloads, not when
 * clicking between pages. Skip / Escape end it immediately.
 */
class SDertIntro extends HTMLElement {
  #ac;
  #done = false;
  #fallback = 0;
  #textTimer = 0;
  #fly = null;

  connectedCallback() {
    let internal = false;
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      const reload = nav && nav.type === 'reload';
      internal = !reload && !!document.referrer && new URL(document.referrer).host === location.host;
    } catch (e) { /* ignore */ }
    if (internal) { this.remove(); return; }

    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.setAttribute('data-ready', '');
    document.documentElement.classList.add('dert-intro-open');

    this.addEventListener('command', (event) => {
      if (event.command === '--skip') this.#finish(true);
    }, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#finish(true);
    }, { signal });

    const video = this.querySelector('[data-intro-video]');
    if (!video) return this.#finish(true);

    video.addEventListener('ended', () => this.#land(), { signal, once: true });
    video.addEventListener('error', () => this.#finish(true), { signal, once: true });
    video.addEventListener('playing', () => this.#scheduleText(video), { signal, once: true });

    // Blurred, silent copy fills the screen behind the sharp video on wide screens.
    const backdrop = this.querySelector('[data-intro-backdrop]');
    if (backdrop) {
      backdrop.muted = true;
      const sync = () => {
        if (Math.abs(backdrop.currentTime - video.currentTime) > 0.15) backdrop.currentTime = video.currentTime;
        if (!video.paused) backdrop.play().catch(() => {});
      };
      video.addEventListener('playing', sync, { signal });
      video.addEventListener('seeked', sync, { signal });
      video.addEventListener('pause', () => backdrop.pause(), { signal });
    }

    // Try with sound first; browsers that block it get the silent version.
    video.muted = false;
    video.volume = 1;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      // Sound blocked until the visitor taps: the clip is only a few seconds long, so
      // playing it silently would finish before anyone could tap. Hold on the first
      // frame with "Tap for sound" and play from the start, with sound, on the first tap.
      attempt.catch(() => this.#holdForSound(video));
    }

    this.#fallback = setTimeout(() => this.#land(), 8000);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#fly?.remove();
    clearTimeout(this.#fallback);
    clearTimeout(this.#textTimer);
    document.documentElement.classList.remove('dert-intro-open');
  }

  #scheduleText(video) {
    const at = parseFloat(this.dataset.textAt);
    const delay = Math.max(0, ((Number.isFinite(at) ? at : 1.2) - video.currentTime) * 1000);
    this.#textTimer = setTimeout(() => this.setAttribute('data-text', ''), delay);
  }

  // Browsers block sound until the visitor taps. While the silent version plays,
  // show "Tap for sound"; the first tap or key press restarts the intro at full volume.
  #offerSound(video) {
    if (this.#done) return;
    this.setAttribute('data-muted', '');
    const { signal } = this.#ac;
    const unmute = (event) => {
      if (this.#done || !this.hasAttribute('data-muted') || this.hasAttribute('data-leaving')) return;
      if (event.type === 'keydown' && event.key === 'Escape') return;
      if (event.target.closest && event.target.closest('.dert-intro__skip')) return;
      this.removeAttribute('data-muted');
      this.removeAttribute('data-text');
      clearTimeout(this.#textTimer);
      clearTimeout(this.#fallback);
      video.currentTime = 0;
      video.muted = false;
      video.volume = 1;
      video.play().then(() => this.#scheduleText(video)).catch(() => {
        // Not allowed yet: keep playing silently and try again on the next tap.
        video.muted = true;
        video.play().catch(() => {});
        this.setAttribute('data-muted', '');
      });
      this.#fallback = setTimeout(() => this.#land(), 8000);
    };
    // Only completed taps/clicks count as permission for sound (touchend/pointerup on phones,
    // click/mousedown on computers), so listen to all of them on the whole page.
    for (const type of ['touchend', 'pointerup', 'click', 'mousedown', 'keydown']) {
      document.addEventListener(type, unmute, { signal, capture: true });
    }
  }

  // Hold on the first frame until a tap allows sound. Nobody taps within 6 seconds:
  // play the silent version so the intro still finishes on its own.
  #holdForSound(video) {
    if (this.#done) return;
    clearTimeout(this.#fallback);
    video.pause();
    video.currentTime = 0;
    this.setAttribute('data-muted', '');
    const { signal } = this.#ac;
    let started = false;
    const start = (event) => {
      if (started || this.#done) return;
      if (event.type === 'keydown' && event.key === 'Escape') return;
      if (event.target.closest && event.target.closest('.dert-intro__skip')) return;
      started = true;
      clearTimeout(this.#fallback);
      this.removeAttribute('data-muted');
      video.currentTime = 0;
      video.muted = false;
      video.volume = 1;
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => this.#finish(true));
      });
      this.#fallback = setTimeout(() => this.#land(), 8000);
    };
    for (const type of ['touchend', 'pointerup', 'click', 'mousedown', 'keydown']) {
      document.addEventListener(type, start, { signal, capture: true });
    }
    this.#fallback = setTimeout(() => {
      if (started || this.#done) return;
      started = true;
      this.removeAttribute('data-muted');
      video.muted = true;
      video.play().catch(() => this.#finish(false));
      this.#fallback = setTimeout(() => this.#land(), 8000);
    }, 6000);
  }

  // Power-saving / data-saver modes block all autoplay. Hold on black and play
  // (with sound) on the first tap; move on to the store if nobody taps.
  #waitForTap(video) {
    if (this.#done) return;
    clearTimeout(this.#fallback);
    this.setAttribute('data-waiting', '');
    const { signal } = this.#ac;
    const start = () => {
      this.removeAttribute('data-waiting');
      video.muted = false;
      video.play().catch(() => this.#finish(true));
      clearTimeout(this.#fallback);
      this.#fallback = setTimeout(() => this.#land(), 8000);
    };
    let started = false;
    const once = () => { if (!started) { started = true; start(); } };
    for (const type of ['touchend', 'pointerup', 'click', 'keydown']) {
      document.addEventListener(type, once, { signal, capture: true });
    }
    this.#fallback = setTimeout(() => this.#finish(false), 6000);
  }

  // Backdrop clears so the logo floats over the store, then the logo glides into the header.
  #land() {
    if (this.#done) return;
    clearTimeout(this.#fallback);
    this.setAttribute('data-leaving', '');

    const video = this.querySelector('[data-intro-video]');
    const targetImg = document.querySelector('.dert-header-logo img');
    if (!targetImg) {
      setTimeout(() => this.#finish(false), 500);
      return;
    }

    const to = targetImg.getBoundingClientRect();
    if (!to.width || !to.height) return this.#finish(false);

    // Fly a copy of the real header logo from centre screen to its spot.
    // It sits outside the blended overlay so it looks exactly like the header logo.
    const fly = targetImg.cloneNode();
    fly.removeAttribute('style');
    fly.removeAttribute('class');
    fly.className = 'dert-intro__fly';
    fly.alt = '';
    // Start big: the logo fills most of the screen, then shrinks into the header.
    const ratio = to.height / to.width;
    let startW = window.innerWidth * 0.85;
    if (startW * ratio > window.innerHeight * 0.6) startW = (window.innerHeight * 0.6) / ratio;
    const startH = startW * ratio;
    fly.removeAttribute('loading');
    if (fly.srcset) fly.sizes = `${Math.ceil(startW)}px`;
    fly.style.width = `${startW}px`;
    fly.style.height = `${startH}px`;
    fly.style.left = `${(window.innerWidth - startW) / 2}px`;
    fly.style.top = `${(window.innerHeight - startH) / 2}px`;
    fly.style.zIndex = '2147483001';
    document.body.append(fly);
    this.#fly = fly;

    const dx = (to.left + to.width / 2) - window.innerWidth / 2;
    const dy = (to.top + to.height / 2) - window.innerHeight / 2;
    const scale = to.width / startW;

    video?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 500, fill: 'forwards' });
    this.querySelector('[data-intro-backdrop]')?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 500, fill: 'forwards' });
    const flight = fly.animate(
      [
        { opacity: 0, transform: 'translate(0, 0) scale(1)', offset: 0 },
        { opacity: 1, transform: 'translate(0, 0) scale(1)', offset: 0.3 },
        { opacity: 1, transform: `translate(${dx}px, ${dy}px) scale(${scale})`, offset: 1 },
      ],
      { duration: 1600, easing: 'cubic-bezier(0.65, 0, 0.25, 1)', fill: 'forwards' }
    );
    flight.finished.then(() => this.#finish(false)).catch(() => this.#finish(false));
  }

  #finish(immediate) {
    if (this.#done) return;
    this.#done = true;
    clearTimeout(this.#fallback);
    clearTimeout(this.#textTimer);
    this.querySelector('[data-intro-video]')?.pause();
    this.#fly?.remove();
    document.documentElement.classList.remove('dert-intro-open');
    if (immediate) { this.remove(); return; }
    this.setAttribute('data-gone', '');
    setTimeout(() => this.remove(), 350);
  }
}

if (!customElements.get('s-dert-intro')) customElements.define('s-dert-intro', SDertIntro);
